(() => {
  const revealClass = 'rhc-scroll-reveal';
  const visibleClass = 'rhc-scroll-reveal--visible';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observedGroups = new WeakSet();
  const groupItems = new WeakMap();

  const groups = [
    {
      root: '.lc-story',
      items: ['.lc-story-panel'],
      observerKey: 'story',
    },
    {
      root: '.lc-newsletter',
      items: [
        '.lc-newsletter__inner > h2',
        '.lc-newsletter__inner > p',
        '.lc-newsletter__form',
      ],
    },
    {
      root: '.lc-footer',
      items: ['.lc-footer__links', '.lc-footer__bottom'],
    },
  ];

  const observers = new Map();

  const clearUnsafeLegacyTargets = (root = document) => {
    if (!(root instanceof Document) && !(root instanceof Element)) return;

    const unsafeSelector = '#MainContent > .shopify-section.rhc-scroll-reveal';
    const unsafeTargets = [];

    if (root instanceof Element && root.matches(unsafeSelector)) unsafeTargets.push(root);
    root.querySelectorAll(unsafeSelector).forEach((element) => unsafeTargets.push(element));

    unsafeTargets.forEach((element) => {
      element.classList.remove(revealClass, visibleClass);
    });
  };

  const revealItems = (items) => {
    items.forEach((item) => item.classList.add(visibleClass));
  };

  const getObserver = (observerKey = 'default') => {
    if (!('IntersectionObserver' in window)) return undefined;
    if (observers.has(observerKey)) return observers.get(observerKey);

    const options = observerKey === 'story'
      ? { rootMargin: '0px', threshold: 0.15 }
      : { rootMargin: '0px 0px -8% 0px', threshold: 0.12 };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealItems(groupItems.get(entry.target) || []);
          observer.unobserve(entry.target);
        }
      },
      options
    );

    observers.set(observerKey, observer);
    return observer;
  };

  const registerGroup = (group, itemSelectors, observerKey = 'default') => {
    if (!(group instanceof HTMLElement) || observedGroups.has(group)) return;

    const items = itemSelectors
      .map((selector) => group.querySelector(selector))
      .filter((item) => item instanceof HTMLElement);

    if (!items.length) return;

    observedGroups.add(group);
    groupItems.set(group, items);

    items.forEach((item, index) => {
      item.classList.add(revealClass);
      item.style.setProperty('--rhc-reveal-delay', `${150 + 150 * index}ms`);
    });

    if (
      reducedMotion.matches ||
      !('IntersectionObserver' in window) ||
      group.getBoundingClientRect().top <= window.innerHeight * 0.82
    ) {
      revealItems(items);
      return;
    }

    getObserver(observerKey)?.observe(group);
  };

  const registerWithin = (root = document) => {
    if (!(root instanceof Document) && !(root instanceof Element)) return;

    clearUnsafeLegacyTargets(root);

    groups.forEach((config) => {
      const roots = new Set();
      if (root instanceof Element && root.matches(config.root)) roots.add(root);
      root.querySelectorAll(config.root).forEach((group) => roots.add(group));
      roots.forEach((group) => registerGroup(group, config.items, config.observerKey));
    });
  };

  const initialize = () => registerWithin(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    registerWithin(event.target);
  });
})();
